import { Doc } from "@/components/Doc/Doc";
import { LabelExplainer } from "@/components/LabelExplainer/LabelExplainer";
import { InputDollar } from "@/components/_pages/ModelPage/Sidebar/InputDollar";
import { InputToggle } from "@/components/_pages/ModelPage/Sidebar/InputToggle";
import {
  isRealEstateInErrorAtom,
  isRealEstateIncludedAtom,
  realEstateBusinessPercentAtom,
  realEstateBusinessValueInputDollarAtom,
  realEstateErrorMessageAtom,
  realEstatePercentAtom,
  realEstateValueInputDollarAtom,
  targetPurchasePriceAtom,
} from "@/financeModels/owasco/general";
import { formatDecimalPercent } from "@/utils/format";
import { lazyReactFallback } from "@/utils/lazy";
import { toNumberOrNull } from "@/utils/math";
import { Alert, Stack } from "@mantine/core";
import { useAtom, useAtomValue } from "jotai";
import { ChangeEvent } from "react";
import { TbBuildingEstate } from "react-icons/tb";
import {
  ComputedResultContainer,
  ComputedResultLine,
} from "../../Sidebar/ComputedResult";

const RealestateIncludedExplainer = lazyReactFallback(
  () => import("@/docs/pages/model/sidebar/realestateIncluded.mdx"),
);
const RealestateBuisnessValueExplainer = lazyReactFallback(
  () => import("@/docs/pages/model/sidebar/realestateBuisnessValue.mdx"),
);

const Panel = () => {
  const targetPurchasePrice = useAtomValue(targetPurchasePriceAtom);
  const [isIncluded, setIncluded] = useAtom(isRealEstateIncludedAtom);

  const [realEstateDollarValue, setRealEstateDollarValue] = useAtom(
    realEstateValueInputDollarAtom,
  );
  const [realEstateBusinessDollarValue, setRealEstateBusinessDollarValue] =
    useAtom(realEstateBusinessValueInputDollarAtom);

  const realEstatePercent = useAtomValue(realEstatePercentAtom);
  const realEstateBusinessPercent = useAtomValue(realEstateBusinessPercentAtom);

  const realEstateErrorMessage = useAtomValue(realEstateErrorMessageAtom);

  return (
    <Stack gap={4}>
      <InputToggle
        label={
          <LabelExplainer
            label="Real Estate Included?"
            explanation={<RealestateIncludedExplainer />}
          />
        }
        checked={isIncluded}
        onChange={(event) => {
          setIncluded(
            (event as ChangeEvent<HTMLInputElement>).currentTarget.checked,
          );
        }}
      />
      {isIncluded && (
        <>
          <InputDollar
            label={
              <LabelExplainer
                label="Real Estate Value"
                explanation={<RealestateBuisnessValueExplainer />}
              />
            }
            value={Number(realEstateDollarValue)}
            onChange={(value) => {
              const realEstateDollar = toNumberOrNull(value) || 0;
              setRealEstateDollarValue(realEstateDollar);
              if (realEstateDollar > Number(targetPurchasePrice)) {
                setRealEstateBusinessDollarValue(0);
                return;
              }
              setRealEstateBusinessDollarValue(
                Math.round(
                  Number(Number(targetPurchasePrice) - realEstateDollar) * 100,
                ) / 100,
              );
            }}
          />

          <InputDollar
            label={
              <LabelExplainer
                label="Business Value"
                explanation={<RealestateBuisnessValueExplainer />}
              />
            }
            value={Number(realEstateBusinessDollarValue)}
            onChange={(value) => {
              const buisnessDollar = toNumberOrNull(value) || 0;
              setRealEstateBusinessDollarValue(buisnessDollar);
              if (buisnessDollar > Number(targetPurchasePrice)) {
                setRealEstateDollarValue(0);
                return;
              }
              setRealEstateDollarValue(
                Math.round(
                  Number(Number(targetPurchasePrice) - buisnessDollar) * 100,
                ) / 100,
              );
            }}
          />

          <ComputedResultContainer>
            <ComputedResultLine
              label={
                <LabelExplainer
                  label="Real Estate (% of Purchase)"
                  explanation={
                    <Doc path="@/docs/pages/model/sidebar/realEstatePercents.mdx" />
                  }
                />
              }
              value={
                toNumberOrNull(realEstatePercent)
                  ? formatDecimalPercent(Number(realEstatePercent))
                  : null
              }
            />
            <ComputedResultLine
              label={
                <LabelExplainer
                  label="Business Value (% of Purchase)"
                  explanation={
                    <Doc path="@/docs/pages/model/sidebar/realEstatePercents.mdx" />
                  }
                />
              }
              value={
                toNumberOrNull(realEstateBusinessPercent)
                  ? formatDecimalPercent(Number(realEstateBusinessPercent))
                  : null
              }
            />
          </ComputedResultContainer>

          {realEstateErrorMessage && (
            <Alert variant="light" color="red" ta={"center"}>
              {realEstateErrorMessage}
            </Alert>
          )}
        </>
      )}
    </Stack>
  );
};

export const ModuleRealEstate = {
  key: "Real Estate",
  icon: <TbBuildingEstate />,
  control: "Real Estate",
  panel: <Panel />,
  errorAtom: isRealEstateInErrorAtom,
};
